import React, { useEffect, useMemo, useState } from 'react';
import { CloseUniversal } from '@duxton-web/icons';
import {
  Button,
  Modal,
  ModalProps,
  notification,
  Tabs,
  Typography,
} from '@duxton-web/ui';
import { useLocation, useNavigate } from 'react-router-dom';

import { variableSettingsEvents } from '@/common/Scribe/EventsList';
import { HTTP_STATUS, VariableType } from '@/constants';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import Canary, {
  isCanaryEligible,
  mapVariableCanaryToConfig,
  validateCanaryConfig,
} from '@/features/variable/settings/canary';
import VariableDetail from '@/features/variable/settings/detail';
import Validation from '@/features/variable/settings/validation';
import {
  setVariable,
  variableDetailUpdate,
} from '@/features/variable/store/actions/variable/action-creators';
import {
  isContextualRulesValid,
  needsContextualRulesException,
} from '@/features/variable/utils';
import Service from '@/lib/App';
import { VariableInfo, VariableMetadata } from '@/lib/PlannerDTOs';
import { sendScribeEvent } from '@/utils/scribe';
import { formatHasAccessErrorGeneric } from '@/utils/url';

import '@/features/variable/settings/create-variable-modal/index.css';

import ContextualRulesExceptionModal from '@/features/variable/settings/contextual-rules-exception-modal';
import { useCreateVariable, useGetCanaryPilotTechFamilies } from '@/hooks';

enum VariableCreationStep {
  DETAIL = 'detail',
  VALIDATION = 'validation',
  CANARY = 'canary',
}

const CreateVariableModalTitle = ({ onCancel }: { onCancel: () => void }) => {
  const variableDispatch = useVariableDispatchContext();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography.Title level={3}>Create Variable</Typography.Title>
      <Button
        data-testid="variable-settings-create-modal-close"
        type="default"
        shape="circle"
        ghost
        size="small"
        onClick={() => {
          variableDispatch(setVariable(null, false, null));

          onCancel();
        }}
        icon={<CloseUniversal />}
      />
    </div>
  );
};

const CreateVariableModal = ({ open, onCancel }: ModalProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const { variable, variableIsValid, jsonSchemaIsValid } = useVariableContext();
  const variableDispatch = useVariableDispatchContext();

  const [currentStep, setCurrentStep] = useState<VariableCreationStep>(
    VariableCreationStep.DETAIL,
  );
  const [isDisplayNone, setIsDisplayNone] = useState<boolean>(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [isValidatingMetrics, setIsValidatingMetrics] = useState(false);

  const { data: canaryConfig } = useGetCanaryPilotTechFamilies({
    enabled: !!open,
  });

  const isCanaryPilotEnrolled = useMemo(() => {
    if (!canaryConfig) return false;
    if (canaryConfig.usePilotTechFamilies === false) return true;
    if (!variable?.techFamily) return false;
    return canaryConfig.techFamilies.includes(variable.techFamily);
  }, [canaryConfig, variable?.techFamily]);

  // True when this new variable will go through the canary step.
  const isCanaryStepShown = isCanaryPilotEnrolled && isCanaryEligible(variable);

  const isCanaryValid = useMemo(() => {
    // Only gate when pilot is enrolled AND variable is canary-eligible.
    if (!isCanaryStepShown) return true;
    const canaryConfigForValidation = mapVariableCanaryToConfig(variable);
    if (!canaryConfigForValidation.enabled) {
      // A reason is required when canary is disabled.
      if (!canaryConfigForValidation.disabledReason) return false;
      // When 'other' is selected, the free-text must be non-empty.
      if (
        canaryConfigForValidation.disabledReason === 'other' &&
        !canaryConfigForValidation.disabledReasonNote?.trim()
      )
        return false;
      return true;
    }
    return validateCanaryConfig(canaryConfigForValidation).isValid;
  }, [isCanaryStepShown, variable?.assignBy, variable?.metadata?.canaryConfig]);

  const { mutate } = useCreateVariable({
    onSuccess: () => {
      notification.success({
        message: `Variable created`,
        description: `Variable ${variable?.name} was successfully created.`,
      });

      sendScribeEvent(variableSettingsEvents.createVariable.name, {
        STATE_NAME: variableSettingsEvents.createVariable.state,
        ...variable,
        error: '',
      });

      onCancel(null);

      navigate(`${pathname}/${variable?.name}/rollout`, { replace: true });
    },
    onError: (err: any) => {
      let description =
        err?.response?.status === HTTP_STATUS.FORBIDDEN
          ? formatHasAccessErrorGeneric(err)
          : err?.message;

      notification.error({
        description,
        message: 'Saving variable failed',
      });

      sendScribeEvent(variableSettingsEvents.createVariable.name, {
        STATE_NAME: variableSettingsEvents.createVariable.state,
        ...variable,
        error: err,
      });
    },
  });

  useEffect(() => {
    if (!open) return;

    const newVariable = new VariableInfo();
    const metadata = new VariableMetadata();
    newVariable.metadata = metadata;

    variableDispatch(setVariable({ data: newVariable }, false, null));
  }, [open]);

  const handleCancel = () => {
    onCancel(null);
  };

  const doCreate = async (exceptionReason?: string) => {
    // Auto-validate all canary metrics and monitors in parallel before creating.
    if (isCanaryStepShown) {
      const cc = variable?.metadata?.canaryConfig;
      // Split mixed metrics[] into query rows and monitor rows (merged format).
      const rawMetrics: any[] = cc?.metrics ?? [];
      const queryRows = rawMetrics.filter((m) => !m.type || m.type === 'query');
      const monitorRowsInMetrics = rawMetrics.filter(
        (m) => m.type === 'monitor' && !!(m.monitorUrl ?? m.monitorURL),
      );
      const allMonitorRows = [
        ...(cc?.monitors ?? []).filter((mon) => !!(mon as any).monitorUrl),
        ...monitorRowsInMetrics,
      ];
      const hasMetrics = queryRows.length > 0;
      const hasMonitors = allMonitorRows.length > 0;
      if (cc?.enabled && (hasMetrics || hasMonitors)) {
        setIsValidatingMetrics(true);
        const varName = variable?.name ?? '';

        const [metricResults, monitorResults] = await Promise.all([
          Promise.all(
            queryRows.map((m) =>
              Service.planner!.validateCanaryQuery({
                query: m.query,
                variable_name: varName,
              }),
            ),
          ),
          Promise.all(
            allMonitorRows.map((mon) =>
              Service.planner!.validateCanaryQuery({
                type: 'monitor',
                variable_name: varName,
                monitor_url: mon.monitorUrl ?? mon.monitorURL,
              }),
            ),
          ),
        ]);

        setIsValidatingMetrics(false);

        const metricFailures = metricResults.some((r) => !r.valid);
        const monitorFailures = monitorResults.some((r) => !r.valid);

        if (metricFailures || monitorFailures) {
          let queryResultIdx = 0;
          const updatedMetrics = rawMetrics.map((m) => {
            if (m.type === 'monitor') return m;
            const r = metricResults[queryResultIdx++];
            return {
              ...m,
              queryValidated: r?.valid ? (m as any).queryValidated : false,
            };
          });

          let monitorResultIdx = 0;
          const updatedMonitors = (cc?.monitors ?? []).map((mon) => {
            if (!(mon as any).monitorUrl) return mon;
            const r = monitorResults[monitorResultIdx++];
            return {
              ...mon,
              monitorValidated: r?.valid
                ? (mon as any).monitorValidated
                : false,
            };
          });

          variableDispatch(
            variableDetailUpdate(
              {
                ...variable,
                metadata: {
                  ...variable?.metadata,
                  canaryConfig: {
                    ...cc,
                    metrics: updatedMetrics,
                    monitors: updatedMonitors,
                  },
                },
              } as VariableInfo,
              variableIsValid,
            ),
          );
          return;
        }
      }
    }

    const variableToCreate =
      exceptionReason !== undefined
        ? {
            ...variable,
            complexity: {
              ...variable?.complexity,
              guardrails: {
                ...variable?.complexity?.guardrails,
                validation: {
                  ...variable?.complexity?.guardrails?.['validation'],
                  exceptionReason,
                },
              },
            },
          }
        : variable;

    mutate(variableToCreate);
  };

  const handleOk = () => {
    if (currentStep === VariableCreationStep.DETAIL) {
      setCurrentStep(VariableCreationStep.VALIDATION);
    } else if (currentStep === VariableCreationStep.VALIDATION) {
      if (isCanaryStepShown) {
        setCurrentStep(VariableCreationStep.CANARY);
      } else if (needsContextualRulesException(variable)) {
        setShowExceptionModal(true);
      } else {
        doCreate();
      }
    } else if (needsContextualRulesException(variable)) {
      setShowExceptionModal(true);
    } else {
      doCreate();
    }
  };

  if (!open) return null;

  return (
    <>
      <ContextualRulesExceptionModal
        open={showExceptionModal}
        actionLabel="Create"
        onConfirm={(reason) => {
          setShowExceptionModal(false);
          doCreate(reason);
        }}
        onCancel={() => setShowExceptionModal(false)}
      />
      <Modal
        data-testid="variable-create-modal"
        className={`variable-create-modal ${isDisplayNone ? 'transparent' : ''}`}
        maskClosable={false}
        centered
        width={800}
        open
        okText={
          currentStep === VariableCreationStep.CANARY ||
          (currentStep === VariableCreationStep.VALIDATION &&
            !isCanaryStepShown)
            ? 'Create'
            : 'Next'
        }
        okButtonProps={{
          size: 'small',
          loading: isValidatingMetrics,
          disabled:
            !variableIsValid ||
            (!jsonSchemaIsValid && variable?.type !== VariableType.BOOLEAN) ||
            !isContextualRulesValid(variable) ||
            (currentStep === VariableCreationStep.CANARY && !isCanaryValid),
        }}
        cancelButtonProps={{
          size: 'small',
        }}
        title={<CreateVariableModalTitle onCancel={handleCancel} />}
        closable={false}
        styles={{
          body: {
            height: 'calc(100vh - 208px)',
            overflowY: 'auto',
          },
        }}
        onCancel={handleCancel}
        onOk={handleOk}
      >
        <Tabs
          activeKey={currentStep}
          onChange={(key) => setCurrentStep(key as VariableCreationStep)}
          items={[
            {
              key: VariableCreationStep.DETAIL,
              label: 'Main Details',
              children: (
                <VariableDetail
                  onManageEntitiesClick={() => {
                    setIsDisplayNone(true);
                  }}
                  onManageEntitiesCancel={() => {
                    setIsDisplayNone(false);
                  }}
                />
              ),
              disabled: false,
            },
            {
              key: VariableCreationStep.VALIDATION,
              label: 'Value Validation',
              children: <Validation />,
              disabled: false,
            },
            ...(isCanaryStepShown
              ? [
                  {
                    key: VariableCreationStep.CANARY,
                    label: 'Canary',
                    children: <Canary />,
                    disabled: false,
                  },
                ]
              : []),
          ]}
        />
      </Modal>
    </>
  );
};

export default CreateVariableModal;
